/**
 * Frontend mirror of the broker-AI subscription tiers — admin-assignable set.
 *
 * Authoritative source: `legal-ai-backend/src/utils/subscriptions/pricing.ts`.
 * Backend validates every assignment at request time; the UI just renders the
 * options + a discounted-amount preview. If pricing changes, update both.
 *
 * Trial + Standard are deliberately excluded — Trial is a signup-flow state
 * (admin cannot assign it), Standard is legacy (no new admin assignments).
 */

export type AdminAssignableTier = 'bronze' | 'silver' | 'gold';

/**
 * Tiers selectable on the admin MANAGED (comp'd) PLAN panel only. Superset of
 * AdminAssignableTier that adds the Gold multiples (Gold 2X/3X/4X) for sponsored
 * orgs needing more than Gold's 100 runs/mo (Trello #280). These are comp'd-only
 * — NOT self-serve assignable as a per-user seat or via the Stitch payment link
 * (those stay on TIER_CODES_ORDERED). Mirrors the backend MANAGED_PLAN_TIERS.
 */
export type ManagedPlanTier = AdminAssignableTier | 'gold_2x' | 'gold_3x' | 'gold_4x';

export interface TierMeta {
  code: ManagedPlanTier;
  displayName: string;
  /** Monthly credit/run quota. */
  monthlyRunsQuota: number;
  /** Monthly price in ZAR rands (decimal). */
  monthlyAmountZAR: number;
}

export const TIER_CATALOGUE: Record<ManagedPlanTier, TierMeta> = {
  bronze: {
    code: 'bronze',
    displayName: 'Bronze',
    monthlyRunsQuota: 20,
    monthlyAmountZAR: 1600,
  },
  silver: {
    code: 'silver',
    displayName: 'Silver',
    monthlyRunsQuota: 50,
    monthlyAmountZAR: 3500,
  },
  gold: {
    code: 'gold',
    displayName: 'Gold',
    monthlyRunsQuota: 100,
    monthlyAmountZAR: 6000,
  },
  // Gold multiples — managed (comp'd) plans only. Runs baked into the tier (2×/
  // 3×/4× Gold). Backend resolves these from MONTHLY_RUN_QUOTA like any tier.
  gold_2x: {
    code: 'gold_2x',
    displayName: 'Gold 2X',
    monthlyRunsQuota: 200,
    monthlyAmountZAR: 12000,
  },
  gold_3x: {
    code: 'gold_3x',
    displayName: 'Gold 3X',
    monthlyRunsQuota: 300,
    monthlyAmountZAR: 18000,
  },
  gold_4x: {
    code: 'gold_4x',
    displayName: 'Gold 4X',
    monthlyRunsQuota: 400,
    monthlyAmountZAR: 24000,
  },
};

/**
 * Tier codes in marketing order (cheapest → most expensive). The SELF-SERVE set
 * — drives the per-user seat dropdown and the Stitch payment-link dropdown. Does
 * NOT include the Gold multiples (those are managed-comp'd only).
 */
export const TIER_CODES_ORDERED: AdminAssignableTier[] = ['bronze', 'silver', 'gold'];

/**
 * Tier codes for the admin MANAGED (comp'd) PLAN dropdown — the self-serve set
 * plus the Gold multiples (Trello #280).
 */
export const MANAGED_TIER_CODES_ORDERED: ManagedPlanTier[] = [
  'bronze',
  'silver',
  'gold',
  'gold_2x',
  'gold_3x',
  'gold_4x',
];

/**
 * Backend's API contract: lowercase string OR null (clears the seat).
 * UI represents the no-tier state as empty string for the Select component.
 */
export type TierSelectValue = AdminAssignableTier | '';
